/**
 * Revenue Performance — CEO-grade widget.
 *
 * Replaces the prior "big green card + four time-window cards"
 * layout with content tuned for a real-estate CEO:
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Header (Revenue Performance + As-of + range selector)        │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │  HERO STRIP — 5 KPI tiles in one row:                         │
 *   │    MTD Revenue | YTD Revenue | Pipeline | Avg Deal | Deals    │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │  Monthly Trend (left, 6-mo bar chart) | Top Projects (right)  │
 *   ├──────────────────────────────────────────────────────────────┤
 *   │  Conversion Rate  ·  Avg Days to Close  ·  Total Revenue YTD  │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * Style departure from the prior emerald-gradient look — now uses a
 * clean white panel with **emerald accent stripe + thin gold rule**
 * to evoke a financial-statement aesthetic.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TrendingUp, TrendingDown, Wallet, Briefcase, Layers,
  Clock, Target, Building2, ArrowUpRight, SlidersHorizontal,
  Megaphone, Zap,
} from 'lucide-react';
import HelpInfo from '@/components/HelpInfo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compact AED — "AED 1.2M" / "AED 850K" / "AED 4,500". Uses the
// Arabic dirham mark when there's no abbreviation so the number stays
// recognizable across the Gulf region.
const fmtAED = (n) => {
  if (n == null || Number.isNaN(Number(n))) return 'AED 0';
  const v = Math.round(Number(n));
  if (Math.abs(v) >= 1_000_000) return `AED ${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000)     return `AED ${(v / 1_000).toFixed(1)}K`;
  return `AED ${v.toLocaleString('en-AE')}`;
};

const fmtInt = (n) => {
  if (n == null) return '0';
  return Number(n).toLocaleString('en-AE');
};

// Compact momentum chip — coloured arrow + percent.
const Momentum = ({ pct, label = 'vs last' }) => {
  if (pct == null) return null;
  const flat = pct === 0;
  const up = pct > 0;
  const cls = flat
    ? 'text-slate-400 bg-slate-50 border-slate-200'
    : up
      ? 'text-emerald-700 bg-emerald-50 border-emerald-100'
      : 'text-rose-600 bg-rose-50 border-rose-100';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${cls}`}>
      {!flat && (up ? <TrendingUp size={10} /> : <TrendingDown size={10} />)}
      {flat ? '0%' : `${up ? '+' : ''}${pct}%`}
      <span className="text-[10px] font-normal opacity-60 ml-0.5">{label}</span>
    </span>
  );
};

// ---- KPI tile ------------------------------------------------------------
// One tile in the hero strip. Compact, no shadow, separated from
// neighbours by a thin vertical divider so the strip reads like a
// statement rather than a grid of cards.
const KpiTile = ({ icon: Icon, label, value, sub, momentum, accent = 'emerald', last }) => {
  const accentMap = {
    emerald: 'text-emerald-600 bg-emerald-50',
    violet:  'text-violet-600  bg-violet-50',
    amber:   'text-amber-600   bg-amber-50',
    blue:    'text-blue-600    bg-blue-50',
    rose:    'text-rose-600    bg-rose-50',
  };
  return (
    <div className={`px-5 py-4 flex-1 min-w-[170px] ${!last ? 'border-r border-slate-100' : ''}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${accentMap[accent]}`}>
          <Icon size={14} />
        </div>
        <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      </div>
      <p className="text-2xl font-bold text-slate-900 tracking-tight tabular-nums">{value}</p>
      <div className="flex items-center justify-between mt-1.5 gap-2 flex-wrap">
        {sub && <p className="text-[11px] text-slate-500">{sub}</p>}
        {momentum != null && <Momentum pct={momentum} />}
      </div>
    </div>
  );
};

// ---- Monthly Trend bar chart --------------------------------------------
// Simple vertical bars with the month label below. Heights are
// proportional to the max value in the window — gives a clean
// "is the year accelerating?" answer at a glance.
const MonthlyTrend = ({ months = [] }) => {
  if (!months.length) {
    return <p className="text-sm text-slate-500">No trend data yet.</p>;
  }
  const max = Math.max(1, ...months.map((m) => m.revenue || 0));
  const lastIdx = months.length - 1;
  return (
    <div className="flex items-end gap-3 h-44 mt-3">
      {months.map((m, i) => {
        const h = Math.max(6, ((m.revenue || 0) / max) * 100);
        const isCurrent = i === lastIdx;
        return (
          <div key={`${m.month}-${i}`} className="flex-1 flex flex-col items-center gap-1.5 group">
            <span className="text-[10px] font-medium text-slate-500 tabular-nums">
              {fmtAED(m.revenue).replace('AED ', '')}
            </span>
            <div
              className={`w-full rounded-t-md transition group-hover:brightness-110 ${
                isCurrent
                  ? 'bg-gradient-to-t from-emerald-500 to-emerald-400'
                  : 'bg-gradient-to-t from-slate-300 to-slate-200'
              }`}
              style={{ height: `${h}%` }}
              title={`${m.month}: ${fmtAED(m.revenue)}`}
            />
            <span className={`text-[11px] tabular-nums ${
              isCurrent ? 'text-emerald-700 font-semibold' : 'text-slate-500'
            }`}>
              {m.month}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ---- Top Projects --------------------------------------------------------
// Horizontal bar list — project name, revenue, deal count + a contribution
// bar showing share of total project revenue.
const TopProjects = ({ projects = [], total = 0 }) => {
  if (projects.length === 0) {
    return (
      <div className="text-center py-10 rounded-xl border border-dashed border-slate-200 bg-slate-50/40">
        <Building2 size={28} className="text-slate-300 mx-auto" strokeWidth={1.5} />
        <p className="text-sm font-medium text-slate-700 mt-2">No project revenue yet</p>
        <p className="text-[11px] text-slate-500 mt-1">Closed deals fill this list once they're tagged to a project.</p>
      </div>
    );
  }
  const PROJECT_COLORS = ['bg-emerald-500', 'bg-blue-500', 'bg-violet-500', 'bg-amber-500', 'bg-rose-500'];
  return (
    <ul className="space-y-3 mt-1">
      {projects.map((p, i) => {
        const pct = Math.max(2, ((p.revenue || 0) / (total || 1)) * 100);
        return (
          <li key={p.name + i} className="group">
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-2 h-2 rounded-full shrink-0 ${PROJECT_COLORS[i % PROJECT_COLORS.length]}`} />
              <span className="text-sm font-medium text-slate-800 truncate flex-1">{p.name}</span>
              <span className="text-[11px] text-slate-500 tabular-nums">{p.deals} {p.deals === 1 ? 'deal' : 'deals'}</span>
              <span className="text-sm font-bold text-slate-900 tabular-nums w-24 text-right">
                {fmtAED(p.revenue)}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div
                className={`h-full rounded-full ${PROJECT_COLORS[i % PROJECT_COLORS.length]} transition-all group-hover:brightness-110`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
};

// ============================================================
// Main widget
// ============================================================
export default function RevenueWidget() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/leadership/revenue`, { params: { token } });
      setData(r.data?.data || null);
    } catch (e) {
      console.warn('leadership/revenue:', e?.response?.data?.detail || e);
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const asOf = data?.as_of
    ? new Date(data.as_of).toLocaleString('en-AE', {
        timeZone: 'Asia/Dubai',
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <Card className="overflow-hidden border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      {/* Top accent stripe — emerald → gold gives a "financial
          statement" feel without dominating the panel. */}
      <div className="h-1 bg-gradient-to-r from-emerald-500 via-emerald-400 to-amber-300" />

      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2 flex-wrap">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-sm">
            <Wallet size={16} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">Revenue Performance</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Headline financials, pipeline health, and where it's coming from
              {asOf && <span className="text-slate-400"> · As of {asOf}</span>}
            </p>
          </div>
          <HelpInfo
            align="inline"
            title="Revenue Performance"
            steps={[
              <><span className="font-medium">MTD / YTD Revenue</span> are closed revenue for the month and year so far, with deltas vs the same span of the previous period.</>,
              <><span className="font-medium">Marketing Spend</span> is what was paid into platforms (Meta, Google, Bayut, Property Finder) — MTD on the tile, with YTD beneath.</>,
              <><span className="font-medium">Pipeline Value</span> sums all open deals — what you could close if every active buyer signs.</>,
              <><span className="font-medium">Avg Deal Size</span> is the typical ticket; useful for forecasting how many deals you need to hit target.</>,
              <><span className="font-medium">ROAS</span> = YTD revenue ÷ YTD spend. Beneath it, Cost per Deal shows what each MTD closure cost in marketing.</>,
              <><span className="font-medium">Conversion Rate</span> + <span className="font-medium">Avg Days to Close</span> show the year-story funnel efficiency.</>,
              <><span className="font-medium">Top Projects</span> shows which buildings are paying the bills this year.</>,
            ]}
            tip="If MTD revenue is red but Marketing Spend is up, your cost per deal is climbing — audit which channels are bleeding before the month closes."
          />
        </div>
        <button
          type="button"
          className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 transition"
          title="Revenue settings"
        >
          <SlidersHorizontal size={13} />
        </button>
      </div>

      {/* Hero KPI strip */}
      {loading && !data ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 divide-x divide-slate-100">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24 mx-3 my-3" />
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap divide-y md:divide-y-0 md:divide-x divide-slate-100">
          <KpiTile
            icon={Wallet}
            label="Revenue MTD"
            value={fmtAED(data?.mtd?.value)}
            sub="Closed this month"
            momentum={data?.mtd?.delta_pct}
            accent="emerald"
          />
          <KpiTile
            icon={Target}
            label="Revenue YTD"
            value={fmtAED(data?.ytd?.value)}
            sub="Year to date"
            momentum={data?.ytd?.delta_pct}
            accent="amber"
          />
          <KpiTile
            icon={Megaphone}
            label="Marketing Spend"
            value={fmtAED(data?.spent_mtd?.value)}
            sub={`MTD · YTD ${fmtAED(data?.spent_ytd)}`}
            momentum={data?.spent_mtd?.delta_pct}
            accent="rose"
            // Rose for spend so it visually contrasts with the
            // emerald revenue tile next to it — a CEO scanning the
            // strip pairs the two colours as "money in / money out".
          />
          <KpiTile
            icon={Briefcase}
            label="Pipeline Value"
            value={fmtAED(data?.pipeline_value)}
            sub={`${fmtInt(data?.pipeline_deals)} open deals`}
            accent="violet"
          />
          <KpiTile
            icon={Layers}
            label="Avg Deal Size"
            value={fmtAED(data?.avg_deal_size)}
            sub="Per booking"
            accent="blue"
          />
          <KpiTile
            icon={ArrowUpRight}
            label="Deals Closed"
            value={fmtInt(data?.deals_closed_mtd?.value)}
            sub="This month"
            momentum={data?.deals_closed_mtd?.delta_pct}
            accent="rose"
            last
          />
        </div>
      )}

      {/* Trend + Projects */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-slate-100 border-t border-slate-100">
        <div className="lg:col-span-7 px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Revenue Trend</p>
              <p className="text-sm text-slate-700 mt-0.5">Closed revenue, last 6 months</p>
            </div>
            <span className="text-[11px] text-emerald-600 font-medium">Current month highlighted</span>
          </div>
          {loading && !data ? (
            <Skeleton className="h-44 mt-3" />
          ) : (
            <MonthlyTrend months={data?.monthly_trend || []} />
          )}
        </div>

        <div className="lg:col-span-5 px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Top Projects (YTD)</p>
              <p className="text-sm text-slate-700 mt-0.5">Revenue contribution by project</p>
            </div>
            <span className="text-[11px] text-slate-500">{(data?.top_projects || []).length} listed</span>
          </div>
          {loading && !data ? (
            <div className="space-y-3 mt-3">
              {[0,1,2,3].map((i) => <Skeleton key={i} className="h-10" />)}
            </div>
          ) : (
            <TopProjects projects={data?.top_projects || []} total={data?.project_total || 0} />
          )}
        </div>
      </div>

      {/* Footer metric strip — four health metrics now that we track
          marketing spend: Conversion / Velocity / ROAS / Total. The
          ROAS card pairs naturally with the Marketing Spend tile in
          the hero strip ("we spent X, we got Y back per dirham"). */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 divide-y md:divide-y-0 md:divide-x divide-slate-100 border-t border-slate-100 bg-slate-50/30">
        <div className="px-5 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <Target size={15} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Conversion Rate</p>
            <p className="text-base font-bold text-slate-900">{(data?.conversion_pct ?? 0).toFixed(1)}%</p>
            <p className="text-[10px] text-slate-500">Inquiries → closed deals</p>
          </div>
        </div>
        <div className="px-5 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
            <Clock size={15} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Avg Days to Close</p>
            <p className="text-base font-bold text-slate-900">{data?.avg_days_to_close ?? 0} days</p>
            <p className="text-[10px] text-slate-500">Inquiry to booking, YTD</p>
          </div>
        </div>
        <div className="px-5 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center">
            <Zap size={15} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">ROAS (YTD)</p>
            <p className="text-base font-bold text-slate-900">
              {data?.roas != null ? `${data.roas}x` : '—'}
            </p>
            <p className="text-[10px] text-slate-500">
              {data?.cost_per_deal
                ? `${fmtAED(data.cost_per_deal)} / deal MTD`
                : 'Revenue per AED spent'}
            </p>
          </div>
        </div>
        <div className="px-5 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center">
            <Wallet size={15} />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Total Revenue</p>
            <p className="text-base font-bold text-slate-900">{fmtAED(data?.total_revenue)}</p>
            <p className="text-[10px] text-slate-500">All-time, closed-won</p>
          </div>
        </div>
      </div>
    </Card>
  );
}
